import React, { FC, useEffect, useState, MouseEvent, useCallback, useRef, useMemo } from 'react';
import { MenuItem, FormControl } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { v4 as uuid } from 'uuid';
import { Standard } from '@getvim/vim-connect';
import { OperationResult, Response, OSActionLog, OSLog } from '@getvim/vim-connect-app';
import {
  sendActionData,
  createLog,
  updateActionLog,
  SELECT_ACTION,
  sortedActions,
  getActionResult,
  removeToString,
} from '../../../services';
import { OPERATION_TYPE_TO_SHORT, ActionsProps, LoaderSide } from '../../../types';
import { getActionPredefinedPayload } from '../../mocks/actions';
import { ButtonLoader, JsonEditor, Selector } from '../common';

const SUBSCRIBE_TELEVISIT = 'SUBSCRIBE_TELEVISIT';

export const Actions: FC<ActionsProps> = ({
  actionData,
  actionResponse,
  localLogs,
  onLogsUpdate,
  onActionDataChange,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [actionType, setActionType] = useState<string>(sortedActions[0]);
  const [payload, setPayload] = useState<any>('');
  const [actionLocalResponse, setActionLocalResponse] = useState<Response | undefined>(undefined);
  const [isActionDataUpdated, setIsActionDataUpdated] = useState<boolean>(false);
  const loadingRef = useRef<boolean>(false);
  const [activeChannel, setActiveChannel] = useState<MessageChannel | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);

  const shouldAttachListener = useMemo(() => actionType === SUBSCRIBE_TELEVISIT, [actionType]);

  const audioRef = useRef<HTMLAudioElement>(null);
  const { addAudioBlob, resetAudioBlobs } = useAudioBlobs(audioRef);
  const [audioChunkCount, setAudioChunkCount] = useState(0);
  const [audioControlEvents, setAudioControlEvents] = useState<any[]>([]);

  const setActionDataFromStorage = useCallback((actionData: any): void => {
    try {
      if (actionData.actionName) {
        setActionType(actionData.actionName.toString());
      }
      if (actionData.payload) {
        const payloadToInject = removeToString(actionData.payload);
        setPayload(payloadToInject);
      }
    } catch (error) {
      console.error('Error setting action data from storage', error);
    }
  }, []);

  useEffect(() => {
    if (actionData && !isActionDataUpdated) {
      setActionDataFromStorage(actionData);
      setIsActionDataUpdated(true);
    }
  }, [actionData, isActionDataUpdated, setActionDataFromStorage]);

  useEffect((): void => {
    if (actionResponse && actionResponse.id === currentRequestId) {
      setActionLocalResponse(actionResponse);
      setIsLoading(false);
      loadingRef.current = false;

      const log: OSLog | undefined = localLogs.find(
        (log: OSLog) => log.parentId === actionResponse.id,
      );
      if (log && !(log as OSActionLog).result) {
        onLogsUpdate([
          updateActionLog(
            localLogs,
            actionResponse.id,
            actionResponse.runtime,
            Date.now(),
            OperationResult.SUCCESS,
            getActionResult(actionResponse),
          ),
        ]);
      }
    }
  }, [actionResponse, localLogs, onLogsUpdate, currentRequestId]);

  const handleActionSelectChange = (e: SelectChangeEvent): void => {
    if (e.target.value === SELECT_ACTION) {
      setActionType(SELECT_ACTION);
      setPayload('');
      return;
    }
    const actionName: Standard.Actions.ActionNames = Standard.Actions.ActionNames[e.target.value];
    setActionType(actionName);
    setPayload(getActionPredefinedPayload(actionName)?.input);
  };

  const handleJsonEditorChange = (newValue: any): void => {
    setPayload(newValue);
  };

  const handleRunClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    setActionLocalResponse(undefined);
    setIsLoading(true);
    loadingRef.current = true;
    const requestId: string = uuid();
    let channel: MessageChannel | null = null;
    setCurrentRequestId(requestId);
    if (shouldAttachListener) {
      resetAudioBlobs();
      setAudioChunkCount(0);
      setAudioControlEvents([]);
      if (activeChannel) {
        activeChannel.port1.close();
      }

      channel = new MessageChannel();
      setActiveChannel(channel);

      // eslint-disable-next-line unicorn/prefer-add-event-listener
      channel.port1.onmessage = (event) => {
        const receivedEvent = event.data.response;
        if (receivedEvent?.type === 'audio-chunk') {
          setAudioChunkCount((count) => count + 1);
          addAudioBlob(receivedEvent.payload.data);
        } else {
          setAudioControlEvents((prev) => [...prev, receivedEvent]);
        }
      };
    }
    window.requestAnimationFrame(() => {
      sendActionData(
        {
          payload: {
            id: requestId,
            actionName: actionType,
            input: payload,
          },
        },
        channel ? channel.port2 : undefined,
      );
      onLogsUpdate([
        ...localLogs,
        createLog(requestId, OPERATION_TYPE_TO_SHORT['action'], actionType, Date.now()),
      ]);
      onActionDataChange({
        actionName: actionType as Standard.Actions.ActionNames,
        payload,
      });
    });
  };
  const showLoading = isLoading && !actionLocalResponse;
  const responseKey = currentRequestId || 'initial';

  return (
    <div className="actions">
      <FormControl size="small">
        <div className="actions-select">
          <Selector
            id="action-selector"
            labelId="action-selector-label"
            value={actionType}
            onChange={handleActionSelectChange}
          >
            {sortedActions.map((key: string) => (
              <MenuItem key={key} value={key}>
                {Standard.Actions.ActionNames[key] ?? key}
              </MenuItem>
            ))}
          </Selector>
        </div>
        <ButtonLoader
          id="action-run"
          className="button action-button"
          onClick={handleRunClick}
          isLoading={isLoading}
          disabled={actionType === SELECT_ACTION}
          loaderSide={LoaderSide.LEFT}
        />
      </FormControl>
      <div></div>
      <div className="action-payload">
        <JsonEditor
          value={payload}
          height={10}
          ignoreInvalidJsonOnChange
          onChange={handleJsonEditorChange}
        />
      </div>
      <div className="action-response" key={responseKey}>
        {showLoading ? (
          <div className="loading-response">Loading response...</div>
        ) : actionLocalResponse ? (
          <>
            <div>Response: (In {actionLocalResponse.runTime} seconds)</div>
            <JsonEditor value={getActionResult(actionResponse)} height={10} />
          </>
        ) : null}
      </div>
      {shouldAttachListener && (
        <div>
          <div>Audio chunks received: {audioChunkCount}</div>
          <audio ref={audioRef} controls />
          <div>Control Events</div>
          {audioControlEvents.map((event, index) => (
            <div key={index}>{JSON.stringify(event)}</div>
          ))}
        </div>
      )}
    </div>
  );
};

Actions.displayName = 'Actions';

const useAudioBlobs = (ref: React.RefObject<HTMLAudioElement>) => {
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  const blobQueueRef = useRef<Blob[]>([]);
  const mime = 'audio/webm; codecs=opus';

  const initialize = useCallback(() => {
    if (!ref.current || mediaSourceRef.current) {
      return;
    }

    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    ref.current.src = URL.createObjectURL(mediaSource);

    mediaSource.addEventListener('sourceopen', () => {
      if (!MediaSource.isTypeSupported(mime)) {
        console.error('MIME type not supported:', mime);
        return;
      }

      const sourceBuffer = mediaSource.addSourceBuffer(mime);
      sourceBuffer.mode = 'sequence';
      sourceBufferRef.current = sourceBuffer;

      sourceBuffer.addEventListener('updateend', () => {
        (async () => {
          if (blobQueueRef.current.length > 0 && !sourceBuffer.updating) {
            const nextBlob = blobQueueRef.current.shift();
            if (!nextBlob) {
              return;
            }
            const buffer = await nextBlob.arrayBuffer();
            try {
              sourceBuffer.appendBuffer(buffer);
            } catch (error) {
              console.error('appendBuffer error:', error);
              blobQueueRef.current.push(nextBlob);
            }
          }
        })();
      });
    });
  }, [ref]);

  useEffect(() => {
    initialize();
  }, [ref, initialize]);

  const addAudioBlob = async (blob: Blob) => {
    const sourceBuffer = sourceBufferRef.current;
    if (!sourceBuffer || sourceBuffer.updating) {
      blobQueueRef.current.push(blob);
      return;
    }

    const buffer = await blob.arrayBuffer();
    try {
      sourceBuffer.appendBuffer(buffer);
    } catch (error) {
      console.error('appendBuffer error:', error);
      blobQueueRef.current.push(blob);
    }
  };

  const resetAudioBlobs = () => {
    blobQueueRef.current = [];
    if (mediaSourceRef.current && sourceBufferRef.current) {
      mediaSourceRef.current.removeSourceBuffer(sourceBufferRef.current);
    }

    sourceBufferRef.current = null;
    mediaSourceRef.current = null;
    initialize();
  };

  return { addAudioBlob, resetAudioBlobs };
};
